import React from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Form,
    Input,
    Row,
    Spinner
} from "reactstrap";
import { Navigate, useNavigate } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

class AdminServicePlanTemplates extends React.Component {
    state = {
        isLoading: false,
        templates: [],
        unauthorized: false,
        createName: "",
        createDescription: "",
        creating: false,
        createSuccess: false,
        createError: null
    };

    componentDidMount() {
        try {
            const customer = sessionStorage.getItem("customer");
            const parsed = customer ? JSON.parse(customer) : null;
            if (parsed?.user?.isAdmin) {
                this.loadTemplates();
            } else {
                this.setState({ unauthorized: true });
            }
        } catch {
            this.setState({ unauthorized: true });
        }
    }

    loadTemplates = () => {
        this.setState({ isLoading: true });
        Api.get("/api/admin/service-plan-templates")
            .then(({ data }) => this.setState({ templates: data || [] }))
            .catch(() => this.setState({ createError: "Unable to load service plan templates." }))
            .finally(() => this.setState({ isLoading: false }));
    };

    handleCreate = (e) => {
        e.preventDefault();
        const name = this.state.createName.trim();
        if (!name) return;

        this.setState({ creating: true, createSuccess: false, createError: null });
        Api.post("/api/admin/service-plan-templates", {
            name,
            description: this.state.createDescription.trim()
        })
            .then(() => {
                if (typeof window.gtag === "function") {
                    window.gtag("event", "admin_service_plan_template_created");
                }
                this.setState({
                    createName: "",
                    createDescription: "",
                    createSuccess: true
                });
                this.loadTemplates();
            })
            .catch(err => this.setState({
                createError: err.response?.data?.message || "Failed to create service plan template."
            }))
            .finally(() => this.setState({ creating: false }));
    };

    render() {
        if (!sessionStorage.getItem("customer")) return sessionEnded();
        if (this.state.unauthorized) return <Navigate to="/" />;

        const { templates, isLoading, createName, createDescription, creating, createSuccess, createError } = this.state;

        return (
            <>
                <SeoNoIndex noindex={true} />

                <div className="position-relative">
                    <section className="section section-lg section-shaped pb-250">
                        <div className="shape shape-style-1 shape-default">
                            <span /><span /><span /><span /><span /><span /><span /><span /><span />
                        </div>
                        <Container className="py-lg-md d-flex">
                            <div className="col px-0">
                                <Row>
                                    <Col lg="8">
                                        <h1 className="display-3 text-white">
                                            Service Plan Templates<br /><span>manage reusable structures</span>
                                        </h1>
                                    </Col>
                                </Row>
                            </div>
                        </Container>
                        <div className="separator separator-bottom separator-skew">
                            <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                                <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                            </svg>
                        </div>
                    </section>
                </div>

                <section className="section section-lg pt-lg-0 mt--200">
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg="12">
                                <Card className="shadow border-0">
                                    <CardBody className="py-5">
                                        <Button color="link" onClick={() => this.props.navigate("/admin")} className="mb-3 p-0">
                                            &larr; Back to menu
                                        </Button>

                                        <h5>Create Template</h5>
                                        {createSuccess && <Alert color="success">Template created successfully.</Alert>}
                                        {createError && <Alert color="danger">{createError}</Alert>}

                                        <Form onSubmit={this.handleCreate} className="mb-4">
                                            <Row>
                                                <Col md="4" className="mb-2">
                                                    <Input
                                                        value={createName}
                                                        onChange={e => this.setState({ createName: e.target.value })}
                                                        placeholder="Template name"
                                                    />
                                                </Col>
                                                <Col md="6" className="mb-2">
                                                    <Input
                                                        value={createDescription}
                                                        onChange={e => this.setState({ createDescription: e.target.value })}
                                                        placeholder="Description"
                                                    />
                                                </Col>
                                                <Col md="2" className="mb-2">
                                                    <Button color="primary" type="submit" disabled={creating || !createName.trim()} block>
                                                        {creating ? "Creating..." : "Create"}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </Form>

                                        {isLoading ? (
                                            <div className="text-center py-4"><Spinner color="primary" /></div>
                                        ) : (
                                            <div className="list-group">
                                                {templates.map(template => (
                                                    <button
                                                        key={template.id}
                                                        type="button"
                                                        className="list-group-item list-group-item-action"
                                                        onClick={() => this.props.navigate(`/admin/service-plan-templates/${template.id}`)}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-start">
                                                            <div>
                                                                <strong>{template.name}</strong>
                                                                <p className="text-muted mb-0">
                                                                    {(template.description || "No description").slice(0, 160)}
                                                                </p>
                                                            </div>
                                                            <div className="text-right ml-3">
                                                                <Badge color={template.active ? "success" : "secondary"}>
                                                                    {template.active ? "Active" : "Inactive"}
                                                                </Badge>
                                                                <div className="text-muted small mt-1">Order {template.sortOrder}</div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                                {templates.length === 0 && <div className="text-muted">No templates found.</div>}
                                            </div>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </>
        );
    }
}

function withNavigateHook(Component) {
    return function Wrapped(props) {
        const navigate = useNavigate();
        return <Component {...props} navigate={navigate} />;
    };
}

export default withNavigateHook(AdminServicePlanTemplates);
